import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode, ReactElement } from 'react';
import ConfirmModal from '../../global/ConfirmModal';

type NavigationHandler = (proceed: () => void) => void;

interface UnsavedChangesContextType {

    subscribe: (handler: NavigationHandler) => () => void;

    runWithConfirmation: (proceed: () => void) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | undefined>(undefined);

interface UnsavedChangesProviderProps {
    children: ReactNode;
}

export const UnsavedChangesProvider: React.FC<UnsavedChangesProviderProps> = ({ children }) => {
    const handlerRef = useRef<NavigationHandler | null>(null);

    const subscribe = useCallback((handler: NavigationHandler) => {
        handlerRef.current = handler;
        return () => {
            if (handlerRef.current === handler) {
                handlerRef.current = null;
            }
        };
    }, []);

    const runWithConfirmation = useCallback((proceed: () => void) => {
        if (handlerRef.current) {
            handlerRef.current(proceed);
        } else {
            proceed();
        }
    }, []);

    return (
        <UnsavedChangesContext.Provider value={{ subscribe, runWithConfirmation }}>
            {children}
        </UnsavedChangesContext.Provider>
    );
};

export const useUnsavedChanges = () => {
    const context = useContext(UnsavedChangesContext);
    if (!context) {
        throw new Error('useUnsavedChanges must be used within an UnsavedChangesProvider');
    }
    return context;
};

interface UseUnsavedChangesGuardOpts {
    hasChanges: boolean;
    save: () => Promise<boolean>;
}

export const useUnsavedChangesGuard = ({
    hasChanges,
    save,
}: UseUnsavedChangesGuardOpts): ReactElement => {
    const { subscribe } = useUnsavedChanges();
    const [pending, setPending] = useState<(() => void) | null>(null);

    const stateRef = useRef({ hasChanges, save });
    stateRef.current = { hasChanges, save };

    useEffect(() => {
        return subscribe((proceed) => {
            if (!stateRef.current.hasChanges) {
                proceed();
                return;
            }
            setPending(() => proceed);
        });
    }, [subscribe]);

    const handleSave = async () => {
        const ok = await stateRef.current.save();
        if (!ok) return;
        const action = pending;
        setPending(null);
        action?.();
    };

    const handleDiscard = () => {
        const action = pending;
        setPending(null);
        action?.();
    };

    const handleClose = () => setPending(null);

    return (
        <ConfirmModal
            open={!!pending}
            title="Unsaved changes"
            message="You have unsaved changes. Would you like to save them before leaving?"
            onConfirm={handleSave}
            onCancel={handleDiscard}
            onClose={handleClose}
            confirmLabel="Save"
            cancelLabel="Discard"
            confirmColor="primary"
        />
    );
};
